import {
  COMPOSITION_CATEGORIES_QUERY,
  COMPOSITION_SUBCATEGORIES_QUERY,
} from '@src/gql/category-gql';
import {
  COMPOSITION_BY_IDS_TEMPLATES_QUERY,
  COMPOSITION_TEMPLATES_QUERY,
} from '@src/gql/compositions-gql';
import { LogManager } from '@src/utility/Logger';
import { fetchAllGraphqlPages } from '@src/utility/paginateGraphql';
import transformKeysToCamelCase from '@src/utility/transformKeysToCamelCase';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';
import type {
  Category,
  CompositionTemplate,
  Subcategory,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { AssetRepository } from '@src/repositories/AssetRepository';
import type { GraphQLClient } from 'graphql-request';

interface CompositionOptions {
  isQuoteCard?: boolean;
}

// The CMA returns a link as a bare id; the app reads `parentComposition.id`.
const toCompositionLink = (id: unknown) =>
  typeof id === 'string' && id ? { id } : undefined;

export class CompositionRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private assetRepository: AssetRepository;
  private logger = LogManager.getLogger('repository:CompositionRepository');

  constructor(
    dClient: Client | ApiClient,
    gqlClient: GraphQLClient,
    assetRepository: AssetRepository,
  ) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
    this.assetRepository = assetRepository;
  }

  async getCompositions(options: CompositionOptions = {}) {
    const { isQuoteCard = false } = options;
    const compositions = await fetchAllGraphqlPages<CompositionTemplate>(
      this.gqlClient,
      COMPOSITION_TEMPLATES_QUERY,
      'allCompositions',
      { isQuoteCard },
    );

    if (isQuoteCard) {
      return compositions.map(composition => ({
        ...composition,
        source: {
          ...composition.source,
          duration: 4,
        },
      }));
    }

    return compositions;
  }

  async getCompositionsByIds(ids: string[], options: CompositionOptions = {}) {
    const { isQuoteCard = false } = options;
    const compositions = await fetchAllGraphqlPages<CompositionTemplate>(
      this.gqlClient,
      COMPOSITION_BY_IDS_TEMPLATES_QUERY,
      'allCompositions',
      { ids, isQuoteCard },
    );

    if (isQuoteCard) {
      return compositions.map(composition => ({
        ...composition,
        source: {
          ...composition.source,
          duration: 4,
        },
      }));
    }
    return compositions;
  }

  async getAllCategoriesWithCompositions(
    whitelistedCategoriesIds: string[],
    whitelistedSubcategoriesIds: string[],
    options: CompositionOptions = {},
  ) {
    const compositions = await this.getCompositions(options);
    return this._getCategoriesWithCompositions(
      compositions,
      whitelistedCategoriesIds,
      whitelistedSubcategoriesIds,
    );
  }

  async getCategoriesWithCompositionsByCompositionIds(
    compositionIds: string[],
    whitelistedCategoriesIds: string[],
    whitelistedSubcategoriesIds: string[],
    options: CompositionOptions = {},
  ) {
    const compositions = await this.getCompositionsByIds(
      compositionIds,
      options,
    );
    return this._getCategoriesWithCompositions(
      compositions,
      whitelistedCategoriesIds,
      whitelistedSubcategoriesIds,
    );
  }

  async getCategoriesWithQuoteCompositionsByCompositionIds(
    compositionIds: string[],
    whitelistedCategoriesIds: string[],
    whitelistedSubcategoriesIds: string[],
  ) {
    return this.getCategoriesWithCompositionsByCompositionIds(
      compositionIds,
      whitelistedCategoriesIds,
      whitelistedSubcategoriesIds,
      { isQuoteCard: true },
    );
  }

  private async _getCategoriesWithCompositions(
    compositions: CompositionTemplate[],
    whitelistedCategoriesIds: string[],
    whitelistedSubcategoriesIds: string[],
  ) {
    const allGraphicSubcategories = await fetchAllGraphqlPages<Subcategory>(
      this.gqlClient,
      COMPOSITION_SUBCATEGORIES_QUERY,
      'allGraphicSubcategories',
      {
        whitelistedCompositionsIds: compositions.map(
          composition => composition.id,
        ),
        whitelistedSubcategoriesIds: whitelistedSubcategoriesIds,
      },
    );

    const allGraphicCategories = await fetchAllGraphqlPages<Category>(
      this.gqlClient,
      COMPOSITION_CATEGORIES_QUERY,
      'allGraphicCategories',
      {
        whitelistedSubcategoriesIds: allGraphicSubcategories.map(
          subcategory => subcategory.id,
        ),
        whitelistedCategoriesIds: whitelistedCategoriesIds,
      },
    );

    const subcategoryWithCompositions = allGraphicSubcategories.map(
      subcategory => ({
        ...subcategory,
        compositions: compositions.filter(composition =>
          subcategory.compositions.some(
            compositionId => compositionId.id === composition.id,
          ),
        ),
      }),
    );

    const categoryWithSubcategories = allGraphicCategories.map(category => ({
      ...category,
      subcategories: subcategoryWithCompositions.filter(subcategory =>
        category.subcategories.some(
          subcategoryId => subcategoryId.id === subcategory.id,
        ),
      ),
    }));

    return categoryWithSubcategories;
  }

  async createCustomCompositionTemplate(
    composition: CompositionTemplate,
    subcategoryId: string,
    personId: string,
    showcaseId: string,
    parentCompositionId: string,
  ) {
    const itemType = await this.dClient.itemTypes.find('composition');
    const response = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      label: composition.label,
      internal_name: composition.internalName,
      thumbnail: composition.thumbnail?.id
        ? { upload_id: composition.thumbnail?.id }
        : null,
      source: JSON.stringify(composition.source),
      // An uploaded media card (PLAT-11732) is made from a file, not copied
      // from a template, so it has no parent to link.
      parent_composition: parentCompositionId || null,
      placement: composition.placement,
      // Kept so a copy stays the kind of card it was copied from — the Replace
      // Intro / End Card pickers list by type.
      ...(composition.compositionType
        ? { composition_type: composition.compositionType }
        : {}),
      controls: JSON.stringify(composition.controls),
      sixteen_by_nine: composition.sixteenByNine
        ? JSON.stringify(composition.sixteenByNine)
        : null,
      nine_by_sixteen: composition.nineBySixteen
        ? JSON.stringify(composition.nineBySixteen)
        : null,
      one_by_one: composition.oneByOne
        ? JSON.stringify(composition.oneByOne)
        : null,
      is_global_preset: false,
      created_by_user: personId,
      created_by_showcase: showcaseId,
      last_updated_by_user: personId,
      is_deleted: false,
      is_fullscreen: composition.isFullscreen,
      is_quote_card: composition.isQuoteCard,
    });

    // add new composition to a subcategory. No need to await response to process flow faster
    this.dClient.items.find(subcategoryId).then((item: unknown) => {
      if (
        item &&
        typeof item === 'object' &&
        'compositions' in item &&
        Array.isArray(item.compositions)
      ) {
        this.dClient.items.update(subcategoryId, {
          compositions: [...(item.compositions || []), response.id],
        });
      }
    });

    // no need to await response
    this.dClient.items.find(showcaseId).then((item: unknown) => {
      const itemType = composition.isQuoteCard
        ? 'quote_cards_branding'
        : 'compositions_branding';
      if (
        item &&
        typeof item === 'object' &&
        itemType in item &&
        Array.isArray(item[itemType as keyof typeof item])
      ) {
        this.dClient.items.update(showcaseId, {
          [itemType]: [
            ...(item[itemType as keyof typeof item] || []),
            response.id,
          ],
        });
      }
    });

    const responseToCamelCase = transformKeysToCamelCase(response);
    return {
      ...responseToCamelCase,
      parentComposition: toCompositionLink(
        responseToCamelCase?.parentComposition,
      ),
      source: JSON.parse(responseToCamelCase?.source),
      thumbnail: undefined,
      controls: JSON.parse(responseToCamelCase?.controls),
      sixteenByNine: responseToCamelCase?.sixteenByNine
        ? JSON.parse(responseToCamelCase?.sixteenByNine)
        : null,
      nineBySixteen: responseToCamelCase?.nineBySixteen
        ? JSON.parse(responseToCamelCase?.nineBySixteen)
        : null,
      oneByOne: responseToCamelCase?.oneByOne
        ? JSON.parse(responseToCamelCase?.oneByOne)
        : null,
    } as unknown as CompositionTemplate;
  }

  async updateCustomCompositionTemplate(
    composition: CompositionTemplate,
    personId: string,
  ) {
    const updatedItem = await this.dClient.items.update(composition.id, {
      item_type: { type: 'item_type', id: 'composition' },
      label: composition.label,
      internal_name: composition.internalName,
      source: JSON.stringify(composition.source),
      parent_composition: composition.parentComposition?.id,
      controls: JSON.stringify(composition.controls),
      sixteen_by_nine: composition.sixteenByNine
        ? JSON.stringify(composition.sixteenByNine)
        : null,
      nine_by_sixteen: composition.nineBySixteen
        ? JSON.stringify(composition.nineBySixteen)
        : null,
      one_by_one: composition.oneByOne
        ? JSON.stringify(composition.oneByOne)
        : null,
      is_global_preset: composition.isGlobalPreset ?? false,
      last_updated_by_user: personId,
    });

    const updatedItemToCamelCase = transformKeysToCamelCase(updatedItem);

    return {
      ...updatedItemToCamelCase,
      parentComposition: toCompositionLink(
        updatedItemToCamelCase?.parentComposition,
      ),
      thumbnail: undefined,
      source: JSON.parse(updatedItemToCamelCase?.source),
      controls: JSON.parse(updatedItemToCamelCase?.controls),
      sixteenByNine: updatedItemToCamelCase?.sixteenByNine
        ? JSON.parse(updatedItemToCamelCase?.sixteenByNine)
        : null,
      nineBySixteen: updatedItemToCamelCase?.nineBySixteen
        ? JSON.parse(updatedItemToCamelCase?.nineBySixteen)
        : null,
      oneByOne: updatedItemToCamelCase?.oneByOne
        ? JSON.parse(updatedItemToCamelCase?.oneByOne)
        : null,
    } as unknown as CompositionTemplate;
  }

  deleteCustomCompositionTemplate(compositionId: string, personId: string) {
    this.dClient.items.update(compositionId, {
      item_type: { type: 'item_type', id: 'composition' },
      is_deleted: true,
      last_updated_by_user: personId,
      deleted_by_user: personId,
      deleted_date: new Date().toISOString(),
    });
  }
}
